import React, {
  createContext,
  useEffect,
  useContext,
  useCallback,
  useState,
} from "react";
import Cookies from "js-cookie";

const UserContext = createContext();
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
const PROFILE_PIC_BUCKET = ENVIRONMENT === "production"
      ? "generativeinsight-user-survey"
      : "generativeinsight-user-survey-dev";
export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [isOrgDeactivated, setIsOrgDeactivated] = useState(null);
  const fetchProfilePicture = useCallback(async (profilePicKey) => {
    if (!profilePicKey) {
      setProfilePicUrl(null);
      return null;
    }

    try {
      const token = Cookies.get("token");
      if (!token) return null;

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/getS3Url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            bucket: PROFILE_PIC_BUCKET,
            key: profilePicKey,
          }),
        },
      );

      const data = await res.json();
      if (data.success && data.preSignedUrl) {
        setProfilePicUrl(data.preSignedUrl);
        return data.preSignedUrl;
      }
      return null;
    } catch (err) {
      console.error("Error fetching profile picture:", err);
      return null;
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    const token = Cookies.get("token");
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/project/getuserPlanAndProject`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          credentials: "include",
        },
      );
      const data = await res.json();
      if (data.status === "success") {
        const roleAPI = data.role
          ? data.role.charAt(0).toUpperCase() + data.role.slice(1).toLowerCase()
          : "User";
        const userType = roleAPI === "Admin" ? "admin" : "user";
        const validProjects =
          data.projectAllowed?.filter(
            (p) => p.status === "completed" || p.status === "running",
          ) || [];

        const user = {
          username: data.username,
          email: data.email,
          user_id: data.user_id || data.id,
          role: data.role,
          roleName: roleAPI,
          userType: userType,
          isAdmin: userType === "admin",
           isSuperAdmin: data.role?.toLowerCase() === "superadmin",
          org_name: data.org_name,
          planDetails: data.planDetails,
          projectAllowed: data.projectAllowed,
          validProjects: validProjects,
          project_count: data.project_count || 0,
          profile_pic: data.profile_pic,
          active_status: data.active_status,
          create_date: data.create_date,
          deactivationStatus: data.deactivationStatus || false,
          orgId: data.orgId || null,
          isOrgDeactivated: data.isOrgDeactivated || null,
          _raw: data,
        };
        setUserData(user);

        if (
          data.deactivationStatus === true &&
          data.deactivationStatusDetail === "COMPLETED"
        ) {
          setIsDeactivated(true);
        }
        setIsOrgDeactivated(data.isOrgDeactivated || null);
        if (data.profile_pic) {
          await fetchProfilePicture(data.profile_pic);
        } else {
          setProfilePicUrl(null);
        }
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfilePicture]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const updateUser = useCallback(
    (partialData) => {
      setUserData((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, ...partialData };
        if (
          partialData.profile_pic &&
          partialData.profile_pic !== prev.profile_pic
        ) {
          fetchProfilePicture(partialData.profile_pic);
        }
        if (partialData.role && partialData.role !== prev.role) {
          const roleAPI =
            partialData.role.charAt(0).toUpperCase() +
            partialData.role.slice(1).toLowerCase();
          updated.roleName = roleAPI;
          updated.userType = roleAPI === "Admin" ? "admin" : "user";
          updated.isAdmin = roleAPI === "Admin";
          updated.isSuperAdmin = partialData.role.toLowerCase() === "superadmin";
        }

        return updated;
      });
    },
    [fetchProfilePicture],
  );

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    await fetchUserData();
  }, [fetchUserData]);

  const resetDeactivated = useCallback(() => {
    setIsDeactivated(false);
  }, []);

  const getInitials = useCallback(() => {
    if (!userData?.username) return "U";
    return userData.username
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }, [userData]);

  const value = {
    userData,
    profilePicUrl,
    isLoading,
    isAdmin: userData?.isAdmin || false,
    isSuperAdmin: userData?.isSuperAdmin || false,
    roleName: userData?.roleName || "User",
    userType: userData?.userType || "user",
    orgId: userData?.orgId || null,
    updateUser,
    isDeactivated,
    resetDeactivated,
    isOrgDeactivated,
    setIsOrgDeactivated,
    refreshUser,
    fetchProfilePicture,
    getInitials,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
